import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatRupiah } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet, TrendingUp, TrendingDown, Package, ArrowUpRight, ArrowDownRight, Banknote, ShoppingCart, ShoppingBag } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="dashboard-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const metrics = [
    { label: "Saldo Kas", value: formatRupiah(data.saldo), icon: Wallet, color: "bg-indigo-600", testId: "metric-saldo" },
    { label: "Total Pemasukan", value: formatRupiah(data.pemasukan), icon: TrendingUp, color: "bg-emerald-500", testId: "metric-pemasukan" },
    { label: "Total Pengeluaran", value: formatRupiah(data.pengeluaran), icon: TrendingDown, color: "bg-rose-500", testId: "metric-pengeluaran" },
    { label: "Nilai Stok", value: formatRupiah(data.nilai_stok), icon: Package, color: "bg-amber-500", testId: "metric-nilai-stok" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan kas dan stok barang Anda</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="dashboard-metrics">
        {metrics.map((m) => (
          <Card key={m.label} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={m.testId}>
            <CardContent className="p-6 flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{m.label}</p>
                <p className="text-xl font-heading font-bold text-slate-900 mt-2">{m.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg ${m.color} flex items-center justify-center shrink-0`}>
                <m.icon className="w-5 h-5 text-white" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="dashboard-daily">
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="metric-total-uang">
          <CardContent className="p-6 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Uang Saya</p>
              <p className="text-xl font-heading font-bold text-slate-900 mt-2" data-testid="metric-total-uang-value">{formatRupiah(data.total_uang)}</p>
              <p className="text-xs text-slate-500 mt-1">Dari semua tempat uang</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
              <Banknote className="w-5 h-5 text-white" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="metric-penjualan-hari-ini">
          <CardContent className="p-6 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Penjualan Hari Ini</p>
              <p className="text-xl font-heading font-bold text-slate-900 mt-2" data-testid="metric-penjualan-hari-ini-value">{formatRupiah(data.penjualan_hari_ini)}</p>
              <p className="text-xs text-slate-500 mt-1" data-testid="metric-penjualan-hari-ini-count">{data.penjualan_hari_ini_count} transaksi</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
              <ShoppingCart className="w-5 h-5 text-white" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid="metric-pembelian-hari-ini">
          <CardContent className="p-6 flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pembelian Hari Ini</p>
              <p className="text-xl font-heading font-bold text-slate-900 mt-2" data-testid="metric-pembelian-hari-ini-value">{formatRupiah(data.pembelian_hari_ini)}</p>
              <p className="text-xs text-slate-500 mt-1" data-testid="metric-pembelian-hari-ini-count">{data.pembelian_hari_ini_count} transaksi</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center shrink-0">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-sm" data-testid="cashflow-chart-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-slate-800">Arus Kas 7 Hari Terakhir</h2>
              <Link to="/kas" className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors" data-testid="view-all-kas-link">
                Lihat Semua
              </Link>
            </div>
            <div className="h-64 min-w-[200px] min-h-[256px]">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={256}>
                <LineChart data={data.cashflow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} stroke="#e2e8f0" />
                  <YAxis tick={{ fontSize: 12, fill: "#64748b" }} stroke="#e2e8f0" tickFormatter={(v) => `${(v / 1000).toFixed(0)}rb`} />
                  <Tooltip formatter={(v) => formatRupiah(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="masuk" name="Pemasukan" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="keluar" name="Pengeluaran" stroke="#f43f5e" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="recent-transactions-card">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Transaksi Terakhir</h2>
            {data.recent.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center" data-testid="recent-empty">Belum ada transaksi</p>
            ) : (
              <div className="space-y-3">
                {data.recent.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors duration-200" data-testid={`recent-transaction-${t.id}`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${t.type === "masuk" ? "bg-emerald-50" : "bg-rose-50"}`}>
                      {t.type === "masuk" ? (
                        <ArrowUpRight className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 text-rose-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{t.description}</p>
                      <p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleDateString("id-ID")}</p>
                    </div>
                    <Badge className={t.type === "masuk" ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-50" : "bg-rose-50 text-rose-600 hover:bg-rose-50"}>
                      {t.type === "masuk" ? "+" : "-"}{formatRupiah(t.amount)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
